/**
 * security-brief.ts — Editorial copy layer for the Security Center standfirst.
 *
 * Mirrors `posture-brief.ts` exactly in shape, seeding, and voice rules. The
 * Security pillar tells a slightly different story: the subject is "Posture",
 * the named risk is the top open SHALL violation (or its framework code), and
 * the weak-link clause is count-driven against the open SHALL violation total
 * rather than the broader "critical issues" definition the Command Center uses.
 *
 * Verdicts cap at six words. Headlines avoid em-dashes (period-then-newline
 * reads cleaner and sidesteps the AI-coded em-dash tell). Variant selection is
 * deterministic against `${tenantId}:${localDay}:${slot}` so the brief stays
 * stable for one tenant across a day and varies across days/tenants.
 */

import {
  cap,
  formatKicker,
  localDayKey,
  pickVariant,
  bucketDelta,
  isNegative,
  isNonNegative,
  type DeltaBucket,
} from './brief-helpers'

/* -------------------------------------------------------------------------- */
/* Public shape                                                               */
/* -------------------------------------------------------------------------- */

export interface SecurityBrief {
  /** Pill above the headline: "Today's Brief · 29 April" */
  kicker: string
  /** Roman portion of the headline, e.g. "Posture is" */
  headlineSubject: string
  /** Italic emphasis word, e.g. "strong" — gets `<em>` treatment */
  headlineModifier: string
  /** Second sentence on the headline line. Capitalized, period-terminated. */
  weakLink: string
  /** Single-paragraph deck under the headline. Uses `**bold**` markers. */
  deck: string
  /** Six-words-or-fewer verdict in the score box, italic, in quotes */
  verdict: string
  /** Byline footer with `**bold**` markers around author + viewer. */
  byline: string
  /** Score delta vs N days ago, drives the green pill */
  delta: { value: number; days: number }
}

/** Lightweight named-risk shape — accepts a SHALL violation or any policy
 *  carrying a framework citation. Both fields optional so the consumer can
 *  pass whatever's available without a normalization layer. */
export interface SecurityTopAction {
  /** Imperative-friendly title, e.g. "Enforce phish-resistant MFA". */
  title: string
  /** Framework citation code, e.g. "SCuBA AAD.4.1v1" or "NIST 800-53". */
  framework?: string
  /** Open-violation criticality. Reserved for future copy variants. */
  criticality?: 'SHALL' | 'SHOULD' | 'MAY'
}

export interface SecurityBriefInput {
  /** Currently signed-in user email, e.g. "adam.jones@iuvotech.com" */
  userEmail?: string
  /**
   * Active tenant ID. Combined with the local day to seed variant selection
   * so the brief is stable per tenant per day. Falls back to a constant when
   * absent (still deterministic, just shared across tenants).
   */
  tenantId?: string
  /** Composite Guardian Score (0-100) — the page surfaces the same composite
   *  the Command Center does, so this is the same value as PostureBrief.score. */
  score?: number | null
  /** 30-day score delta, drives modifier choice and the score-box pill. */
  deltas?: { score?: number; days?: number }
  /** Top open SHALL violation (or other named risk) — drives the deck risk
   *  sentence and, when present alongside non-trivial criticalCount, names
   *  the framework as the risk in the weak-link clause. */
  topAction?: SecurityTopAction
  /** Open SHALL-violation count — drives the weak-link clause. */
  criticalCount?: number
  /** Override "today" — primarily for testing the kicker. */
  asOfDate?: Date
}

/* -------------------------------------------------------------------------- */
/* Internal helpers                                                           */
/* -------------------------------------------------------------------------- */

/** Drop trailing punctuation a violation title may carry through ingest. */
function trimTrailingPunct(s: string): string {
  return s.replace(/[.,;:!?]+$/, '')
}

/* -------------------------------------------------------------------------- */
/* Verdict variants (six-words-or-fewer per the ScoreBox cap)                 */
/* -------------------------------------------------------------------------- */

const VERDICT_EXCELLENT: readonly string[] = [
  'Strong across the board.',
  'Posture holds steady today.',
  'Strong shape. Routine watch only.',
  'Strong posture. Keep it steady.',
]

/** Bands 70-84 with no critical SHALL items pending. */
const VERDICT_STRONG_NO_WEAKLINK: readonly string[] = [
  'Strong. One small soft spot.',
  'Solid. One small soft spot.',
  'Healthy posture. One thing to watch.',
]

/** Bands 70-84 with named risk in play. Templates take a `{framework}` token
 *  that the verdict picker collapses to its first whitespace-separated segment
 *  so the six-word cap holds even when callers pass a "SCuBA AAD.4.1v1" pair. */
const VERDICT_STRONG_FRAMEWORK: readonly string[] = [
  '{framework} is the soft spot.',
  'Tighten {framework} next.',
  'Healthy. {framework} stands out.',
]

/** High score (≥70) AND slipping/falling delta. Previously routed to
 *  STRONG_* or EXCELLENT, which read "Strong across the board" while the
 *  trend sentence said the score had dropped. */
const VERDICT_HIGH_SLIPPING: readonly string[] = [
  'Strong, but softening.',
  'Posture is slipping.',
  'High base, easing back.',
]

/** Templates take a `{framework}` token. High score + slipping + named
 *  framework — pairs the framework with a slipping-state read. */
const VERDICT_HIGH_SLIPPING_FRAMEWORK: readonly string[] = [
  'Slipping. Watch {framework}.',
  '{framework} is starting to slip.',
  'Softening. {framework} is the drag.',
]

/** Bands 55-69. Templates take a `{verb}` token (capitalized imperative). */
const VERDICT_MIXED: readonly string[] = [
  'Mixed. Tighten Best Practice first.',
  '{verb} first, the rest can wait.',
  'Soft posture. {verb} first.',
]

/** Band <55. */
const VERDICT_FRAGILE: readonly string[] = [
  'Fragile. Triage SHALL violations now.',
  'Soft floor. Begin with SHALLs.',
  'Soft posture. Address SHALLs first.',
  'Several SHALLs. Triage first.',
]

/** Imperative verb library. Matches the posture-brief approach but keeps the
 *  verbs that read naturally for security work. */
function imperativeFromAction(title?: string): string {
  // Security-flavored default: "tighten" reads like the kind of imperative
  // a security advisor would use ("tighten Best Practice first") whereas
  // "triage" is what the broader Posture brief defaults to.
  if (!title) return 'tighten'
  const first = title.trim().split(/\s+/)[0]?.toLowerCase().replace(/[^a-z]/g, '') ?? ''
  const known = new Set([
    'enforce',
    'roll',
    'reclaim',
    'review',
    'remove',
    'rotate',
    'restrict',
    'require',
    'rebuild',
    'remediate',
    'audit',
    'patch',
    'disable',
    'block',
    'tighten',
    'triage',
    'address',
    'harden',
    'close',
    'lock',
  ])
  return known.has(first) ? first : 'tighten'
}

function pickVerdict(
  score: number,
  bucket: DeltaBucket,
  _criticalCount: number | undefined,
  topAction: SecurityTopAction | undefined,
  seed: string,
): string {
  // High band + slipping: acknowledge the decline rather than routing to
  // "strong across the board". Otherwise high score reads excellent.
  if (score >= 85) {
    if (isNegative(bucket)) {
      if (topAction?.framework) {
        const shortFramework =
          topAction.framework.trim().split(/\s+/)[0] ?? topAction.framework
        const tpl = pickVariant(VERDICT_HIGH_SLIPPING_FRAMEWORK, seed, 'verdict.highSlippingFramework')
        return tpl.replace('{framework}', shortFramework)
      }
      return pickVariant(VERDICT_HIGH_SLIPPING, seed, 'verdict.highSlipping')
    }
    return pickVariant(VERDICT_EXCELLENT, seed, 'verdict.excellent')
  }

  if (score >= 70) {
    if (isNegative(bucket)) {
      if (topAction?.framework) {
        const shortFramework =
          topAction.framework.trim().split(/\s+/)[0] ?? topAction.framework
        const tpl = pickVariant(VERDICT_HIGH_SLIPPING_FRAMEWORK, seed, 'verdict.highSlippingFramework')
        return tpl.replace('{framework}', shortFramework)
      }
      return pickVariant(VERDICT_HIGH_SLIPPING, seed, 'verdict.highSlipping')
    }
    if (topAction?.framework) {
      // Collapse "SCuBA AAD.4.1v1" → "SCuBA" so the six-word verdict cap
      // holds even with a multi-token framework citation. The full citation
      // still appears in the deck / weak-link where there's room for it.
      const shortFramework = topAction.framework.trim().split(/\s+/)[0] ?? topAction.framework
      const tpl = pickVariant(VERDICT_STRONG_FRAMEWORK, seed, 'verdict.framework')
      return tpl.replace('{framework}', shortFramework)
    }
    return pickVariant(VERDICT_STRONG_NO_WEAKLINK, seed, 'verdict.strong')
  }

  if (score >= 55) {
    const verb = cap(imperativeFromAction(topAction?.title))
    const tpl = pickVariant(VERDICT_MIXED, seed, 'verdict.mixed')
    return tpl.replace('{verb}', verb)
  }

  return pickVariant(VERDICT_FRAGILE, seed, 'verdict.fragile')
}

/* -------------------------------------------------------------------------- */
/* Headline modifier (italic emphasis word — kept as single-word table)       */
/* -------------------------------------------------------------------------- */

/**
 * Security pillar modifier band table — delta-aware at every band so the
 * headline modifier never contradicts the deck trend sentence.
 *
 *   ≥85 with rising delta     → "strong"
 *   ≥85 with flat delta       → "steady"
 *   ≥85 with slipping/falling → "slipping"
 *   70-84 with rising delta   → "steady"
 *   70-84 with flat delta     → "mixed"
 *   70-84 with slipping/falling → "slipping"
 *   55-69 with rising delta   → "recovering"
 *   55-69 with flat delta     → "mixed"
 *   55-69 with slipping/falling → "weakening"
 *   <55 with rising delta     → "recovering"
 *   <55 with flat/negative    → "fragile"
 */
function pickModifier(score: number, bucket: DeltaBucket): string {
  if (score >= 85) {
    if (bucket === 'risingFast' || bucket === 'rising') return 'strong'
    if (isNegative(bucket)) return 'slipping'
    return 'steady'
  }
  if (score >= 70) {
    if (bucket === 'risingFast' || bucket === 'rising') return 'steady'
    if (isNegative(bucket)) return 'slipping'
    return 'mixed'
  }
  if (score >= 55) {
    if (bucket === 'risingFast' || bucket === 'rising') return 'recovering'
    if (isNegative(bucket)) return 'weakening'
    return 'mixed'
  }
  if (bucket === 'risingFast' || bucket === 'rising') return 'recovering'
  return 'fragile'
}

/* -------------------------------------------------------------------------- */
/* Weak-link sentence                                                         */
/* -------------------------------------------------------------------------- */

const WEAKLINK_ZERO: readonly string[] = [
  'No SHALL violations on the board.',
  'Nothing critical on the board.',
  'The critical lane is clear.',
]

const WEAKLINK_ONE: readonly string[] = [
  'One soft spot to close.',
  'One soft spot stands out.',
  'One SHALL violation is dragging the score.',
]

/** Templates take an `{n}` token (word form: "Two" / "Three"). */
const WEAKLINK_FEW: readonly string[] = [
  '{n} soft spots stand out.',
  '{n} SHALL violations to close this week.',
  '{n} items are dragging the score down.',
]

/** Templates take a `{framework}` token (e.g. "SCuBA AAD.4.1v1"). */
const WEAKLINK_FRAMEWORK: readonly string[] = [
  '{framework} is the named risk.',
  '{framework} is the one carrying the weight.',
  '{framework} needs eyes on it this week.',
]

const WEAKLINK_BROAD: readonly string[] = [
  'Multiple SHALL items in play.',
  'SHALL violations are stacking up.',
  'Posture is moving the wrong way.',
]

/** Neutral broad pool — used when 4+ SHALL items are open but the score
 *  isn't actually declining. Pre-fix the BROAD pool fired anyway, pairing
 *  "stacking up" with a flat or improving trend. */
const WEAKLINK_BROAD_NEUTRAL: readonly string[] = [
  'Several SHALLs still need attention.',
  'More than one critical gap is open.',
  'A few SHALL items are still on the board.',
]

const WEAKLINK_TIGHTEN: readonly string[] = [
  'Room to tighten.',
  'Some routine cleanup left.',
  'Hygiene work to round it out.',
]

function buildWeakLink(
  criticalCount: number | undefined,
  bucket: DeltaBucket,
  topAction: SecurityTopAction | undefined,
  seed: string,
): string {
  const numberWord = (n: number): string =>
    ['zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six'][n] ?? String(n)

  if (typeof criticalCount === 'number' && criticalCount > 0) {
    if (criticalCount === 1) return pickVariant(WEAKLINK_ONE, seed, 'weakLink.one')
    if (criticalCount <= 3) {
      const tpl = pickVariant(WEAKLINK_FEW, seed, 'weakLink.few')
      return tpl.replace('{n}', numberWord(criticalCount))
    }
    // 4+ SHALL items. Only narrate worsening ("stacking up") when the trend
    // bucket actually says so — otherwise the language contradicts a flat or
    // improving deck trend in the same brief.
    if (isNegative(bucket)) {
      if (topAction?.framework) {
        const tpl = pickVariant(WEAKLINK_FRAMEWORK, seed, 'weakLink.framework')
        return tpl.replace('{framework}', topAction.framework)
      }
      return pickVariant(WEAKLINK_BROAD, seed, 'weakLink.broad')
    }
    return pickVariant(WEAKLINK_BROAD_NEUTRAL, seed, 'weakLink.broadNeutral')
  }

  if (typeof criticalCount === 'number' && criticalCount === 0) {
    return pickVariant(WEAKLINK_ZERO, seed, 'weakLink.zero')
  }

  return pickVariant(WEAKLINK_TIGHTEN, seed, 'weakLink.tighten')
}

/* -------------------------------------------------------------------------- */
/* Deck (trend + named risk + recommended action)                             */
/* -------------------------------------------------------------------------- */

const TREND_LIFTING_FAST: readonly string[] = [
  'Best Practice, Secure Score, and SCuBA controls have all moved in the right direction over the past 30 days.',
  'All three security signals improved over the past 30 days. Real momentum here.',
  'The score climbed steadily over the past month, lifted by gains across Best Practice and Secure Score.',
]

const TREND_LIFTING: readonly string[] = [
  'Posture has ticked up over the past 30 days, with steady progress on Best Practice and Secure Score.',
  'Small gains over the past 30 days, spread across the security signals. Things are moving the right way.',
  'The score is up modestly over the past month. Quiet progress.',
]

const TREND_FLAT: readonly string[] = [
  'Posture has held flat over the past 30 days. Not gaining ground, not losing it either.',
  "The score hasn't moved over the past 30 days. Same story as last month.",
  'No real movement over the past month. The same signals are still doing the work.',
]

const TREND_SLIPPING: readonly string[] = [
  'Posture has slipped slightly over the past 30 days. Worth a look when you can.',
  'The score is down a touch over the past month. Worth a look when you can.',
  'Modest decline over the past 30 days. Not yet a trend, but keep an eye on it.',
]

const TREND_FALLING: readonly string[] = [
  'Posture has fallen meaningfully over the past 30 days. Needs eyes on it this week.',
  'The score dropped sharply over the past month. Needs eyes on it this week.',
  'Significant decline across the past 30 days. This needs a real response, not next sprint.',
]

/** Templates take a `{phrase}` token (already-bolded action title). */
const RISK_TOP_ACTION: readonly string[] = [
  '{phrase} is the biggest move you can make right now, and the one that lifts the score most.',
  '{phrase} is where your time pays back fastest right now.',
  '{phrase} is the one to close first to reclaim score.',
]

/** Templates take a `{framework}` token (already-bolded). */
const RISK_FRAMEWORK: readonly string[] = [
  "{framework} is the framework that's quietly dragging things, and where your time pays back fastest.",
  '{framework} is the lagging framework; tightening there moves the score most.',
  '{framework} is the one slowing you down right now.',
]

const RISK_HYGIENE: readonly string[] = [
  "Nothing is shouting for attention. What's left is hygiene work.",
  'The remaining items are hygiene, not headline risks.',
  "No single gap is leading the story. It's housekeeping from here.",
]

const RISK_COMPOUND: readonly string[] = [
  'Several smaller gaps add up; closing the biggest of them lifts the score fastest.',
  'A handful of smaller items stack up. The biggest one is where to start.',
  'A handful of smaller gaps add up. The biggest of them is the place to start.',
]

const ACTION_HOLD: readonly string[] = [
  "Keep doing what you're doing. The routine reviews are working.",
  'Stay the course this week.',
  'Keep the usual review rhythm.',
]

const ACTION_LEAD: readonly string[] = [
  'Close the lead item this week to keep momentum.',
  'Take the lead item this week.',
  'Knock out the lead item this week to maintain momentum.',
]

const ACTION_TRIAGE: readonly string[] = [
  'Take the biggest items first; the rest can wait until next sprint.',
  'Focus on what moves the score most first; the rest holds until next sprint.',
  'Take the high-impact items first and let the rest wait.',
]

const ACTION_PUSH: readonly string[] = [
  'This one needs a real week of focus, not the usual cadence.',
  'Plan a focused remediation push this sprint.',
  'This is a remediation sprint, not business-as-usual.',
]

function buildDeck(
  score: number,
  bucket: DeltaBucket,
  criticalCount: number | undefined,
  topAction: SecurityTopAction | undefined,
  seed: string,
): string {
  const trend = (() => {
    if (bucket === 'risingFast') return pickVariant(TREND_LIFTING_FAST, seed, 'deck.trend.liftingFast')
    if (bucket === 'rising') return pickVariant(TREND_LIFTING, seed, 'deck.trend.lifting')
    if (bucket === 'flat') return pickVariant(TREND_FLAT, seed, 'deck.trend.flat')
    if (bucket === 'slipping') return pickVariant(TREND_SLIPPING, seed, 'deck.trend.slipping')
    return pickVariant(TREND_FALLING, seed, 'deck.trend.falling')
  })()

  const risk = (() => {
    if (topAction?.title) {
      const phrase = `**${trimTrailingPunct(topAction.title)}**`
      const tpl = pickVariant(RISK_TOP_ACTION, seed, 'deck.risk.topAction')
      return tpl.replace('{phrase}', phrase)
    }
    if (topAction?.framework) {
      const framework = `**${topAction.framework}**`
      const tpl = pickVariant(RISK_FRAMEWORK, seed, 'deck.risk.framework')
      return tpl.replace('{framework}', framework)
    }
    if (score >= 85 && (!criticalCount || criticalCount === 0)) {
      return pickVariant(RISK_HYGIENE, seed, 'deck.risk.hygiene')
    }
    return pickVariant(RISK_COMPOUND, seed, 'deck.risk.compound')
  })()

  // Action routing is trend-first. The previous `score >= 90 → HOLD` rule
  // could pair "Keep the existing review cadence steady" with "needs eyes
  // on it this week" when the deck trend was actually falling.
  const action = (() => {
    if (bucket === 'falling') return pickVariant(ACTION_PUSH, seed, 'deck.action.push')
    if (bucket === 'slipping') return pickVariant(ACTION_LEAD, seed, 'deck.action.lead')
    const calm =
      score >= 90 && isNonNegative(bucket) && (criticalCount ?? 0) <= 3
    if (calm) return pickVariant(ACTION_HOLD, seed, 'deck.action.hold')
    if (score >= 75) return pickVariant(ACTION_LEAD, seed, 'deck.action.lead')
    if (score >= 60) return pickVariant(ACTION_TRIAGE, seed, 'deck.action.triage')
    return pickVariant(ACTION_PUSH, seed, 'deck.action.push')
  })()

  return `${trend} ${risk} ${action}`
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose a SecurityBrief from the active Security Center state. All inputs
 * are optional — the function degrades gracefully toward a plausible "no data"
 * brief rather than throwing.
 *
 * When `input.score` is null/undefined (e.g. the page mounted before the
 * score query resolved) we return a neutral "reading" brief rather than
 * synthesizing a worst-band "fragile / SHALL triage" verdict. Without this
 * guard the composer would coerce `null → 0`, which lands the loading state
 * in the lowest editorial band and shows the user an authoritative-but-wrong
 * verdict for a fraction of a second before the real data arrives.
 *
 * Note: `score === 0` is a legitimate real value and still routes through
 * the normal band logic — only `null`/`undefined` triggers the loading brief.
 */
export function composeSecurityBrief(input: SecurityBriefInput = {}): SecurityBrief {
  const userEmail = input.userEmail ?? 'you'
  const now = input.asOfDate ?? new Date()
  const deltaValue = typeof input.deltas?.score === 'number' ? input.deltas.score : 0
  const deltaDays = typeof input.deltas?.days === 'number' ? input.deltas.days : 30

  const byline =
    userEmail === 'you'
      ? 'Brief by **Guardian Continuous Assessment**'
      : `Brief by **Guardian Continuous Assessment** · For **${userEmail}**`

  if (input.score == null) {
    return {
      kicker: formatKicker(now),
      headlineSubject: 'Posture is',
      headlineModifier: 'loading',
      weakLink: 'Reading the latest signal.',
      deck: 'Pulling the latest posture data. The brief updates once the scan resolves.',
      verdict: 'Reading posture…',
      byline,
      delta: { value: deltaValue, days: deltaDays },
    }
  }

  const score = input.score
  const bucket = bucketDelta(deltaValue)

  // Daily seed: stable per (tenant, local day). Tenants share a seed when
  // unset (still deterministic for tests + anonymous flows).
  const seed = `${input.tenantId ?? 'guardian'}:${localDayKey(now)}`

  const headlineModifier = pickModifier(score, bucket)
  const weakLink = buildWeakLink(input.criticalCount, bucket, input.topAction, seed)
  const verdict = pickVerdict(score, bucket, input.criticalCount, input.topAction, seed)
  const deck = buildDeck(score, bucket, input.criticalCount, input.topAction, seed)

  return {
    kicker: formatKicker(now),
    headlineSubject: 'Posture is',
    headlineModifier,
    weakLink,
    deck,
    verdict,
    byline,
    delta: { value: deltaValue, days: deltaDays },
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Variant tables exposed for shape-assertions in tests. Not part of the
 *  public runtime API; do not import from app code. */
export const __TEST_ONLY__ = {
  VERDICT_EXCELLENT,
  VERDICT_STRONG_NO_WEAKLINK,
  VERDICT_STRONG_FRAMEWORK,
  VERDICT_HIGH_SLIPPING,
  VERDICT_HIGH_SLIPPING_FRAMEWORK,
  VERDICT_MIXED,
  VERDICT_FRAGILE,
  WEAKLINK_ZERO,
  WEAKLINK_ONE,
  WEAKLINK_FEW,
  WEAKLINK_FRAMEWORK,
  WEAKLINK_BROAD,
  WEAKLINK_BROAD_NEUTRAL,
  WEAKLINK_TIGHTEN,
}
